import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { OAuthStartResponse, WorkspaceStatus } from "@/types";

interface ScopeCatalogue {
  read: string[];
  write: string[];
}

export function useWorkspaceStatus() {
  return useQuery<WorkspaceStatus>({
    queryKey: ["workspace", "status"],
    queryFn: async () => {
      const res = await apiClient.get<WorkspaceStatus>("/oauth/google/status");
      return res.data;
    },
  });
}

export function useGoogleScopeCatalogue() {
  return useQuery<ScopeCatalogue>({
    queryKey: ["oauth", "google", "scopes"],
    queryFn: async () => {
      const res = await apiClient.get<ScopeCatalogue>("/oauth/google/scopes");
      return res.data;
    },
  });
}

/**
 * Mutation: ask the backend to mint an OAuth state row + return Google's
 * authz URL. The component then sets `window.location.assign(url)` so the
 * browser navigates to Google for consent.
 */
export function useStartWorkspaceConnect() {
  return useMutation({
    mutationFn: async (params?: { scopes?: string[]; redirect_to?: string }) => {
      const res = await apiClient.post<OAuthStartResponse>("/oauth/google/start", params ?? {});
      return res.data;
    },
  });
}

export function useDisconnectWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.post("/oauth/google/disconnect", {});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspace", "status"] }),
  });
}
